/*
 * trainerResources.ts — the trainer's stardust and per-family candy, as read
 * off their own Pokémon pages during scans.
 *
 * 2026-08-26: the Stardust Planner now DISPLAYS the stardust balance and
 * budgets against it (owner ask), superseding the 2026-08-20 keep-it-hidden
 * direction — which was always "until recommendations can spend it". The
 * original rationale stands below:
 *
 * This ledger exists so
 * future recommendations can spend-check themselves. Stardust is hard to earn
 * and a "power this up" call that ignores whether the trainer can afford it,
 * or a "transfer for candy" call for a family already sitting on 1,400
 * candies, is advice from someone who wasn't listening.
 *
 * Every caught-Pokémon page states the wallet stardust and the family's candy
 * counts, so scans keep this fresh for free. Candy is keyed by the FAMILY
 * BASE species id ("SLAKOTH CANDY" on a Slaking → key 287), resolved through
 * the same canonical name table the scanner uses. Values are device-local,
 * last-read-wins with a timestamp.
 */

const STORAGE_KEY = "nexus.trainerResources.v1";

export interface FamilyCandy {
  candy: number | null;
  candyXl: number | null;
  updatedAt: number;
}

export interface TrainerResources {
  stardust: number | null;
  stardustUpdatedAt: number;
  /**
   * Where the stardust value came from. `stardustUpdatedAt` is WHEN THE GAME
   * SHOWED IT (2026-09-03): a live scan's frame time, an uploaded
   * screenshot's photo time, a manual entry's moment. The newest sighting
   * wins, whichever route it came by, so a screenshot taken after the last
   * scan supersedes the scan and an old screenshot never overwrites a newer
   * reading.
   */
  stardustSource?: "scan" | "screenshot" | "manual" | null;
  /** Keyed by the candy family's BASE species id. */
  candyByFamily: Record<number, FamilyCandy>;
  /**
   * Mega Energy per species (2026-09-03), keyed by the species the page
   * names ("METAGROSS MEGA ENERGY" on a Metang page → 376). Read off the
   * same caught-Pokémon page as the candy.
   */
  megaEnergyBySpecies?: Record<number, { energy: number; updatedAt: number }>;
  /**
   * Mega Energy per Mega FORM (2026-09-03 owner ask): GO keeps X and Y
   * energy apart ("MEWTWO MEGA ENERGY X" / "... Y" on the same page), so
   * the species map above cannot hold both. Keyed by megaFormKey ("150",
   * "150:mewtwo-mega-y"). The species map still carries the base form's
   * reading for older readers.
   */
  megaEnergyByForm?: Record<string, { energy: number; updatedAt: number }>;
}

/** One Mega Energy figure off a page, with the X/Y letter GO prints beside it. */
export interface MegaEnergyObservation {
  speciesId: number;
  /** "" for the base Mega, "X" or "Y" for a pair. */
  formLabel: string;
  energy: number;
}

/** Live-update event, fired on every write (the Planner subscribes). */
export const TRAINER_RESOURCES_EVENT = "nexus:trainer-resources";

const EMPTY: TrainerResources = {
  stardust: null,
  stardustUpdatedAt: 0,
  candyByFamily: {},
};

let cache: TrainerResources | null = null;

function read(): TrainerResources {
  if (cache) return cache;
  try {
    if (typeof window === "undefined") return (cache = { ...EMPTY, candyByFamily: {} });
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return (cache = { ...EMPTY, candyByFamily: {} });
    const parsed = JSON.parse(raw) as Partial<TrainerResources>;
    return (cache = {
      stardust: typeof parsed.stardust === "number" ? parsed.stardust : null,
      stardustUpdatedAt:
        typeof parsed.stardustUpdatedAt === "number" ? parsed.stardustUpdatedAt : 0,
      stardustSource:
        parsed.stardustSource === "manual" ||
        parsed.stardustSource === "scan" ||
        parsed.stardustSource === "screenshot"
          ? parsed.stardustSource
          : null,
      candyByFamily:
        parsed.candyByFamily && typeof parsed.candyByFamily === "object"
          ? (parsed.candyByFamily as Record<number, FamilyCandy>)
          : {},
      megaEnergyBySpecies:
        parsed.megaEnergyBySpecies && typeof parsed.megaEnergyBySpecies === "object"
          ? (parsed.megaEnergyBySpecies as TrainerResources["megaEnergyBySpecies"])
          : {},
      megaEnergyByForm:
        parsed.megaEnergyByForm && typeof parsed.megaEnergyByForm === "object"
          ? (parsed.megaEnergyByForm as TrainerResources["megaEnergyByForm"])
          : {},
    });
  } catch {
    return (cache = { ...EMPTY, candyByFamily: {} });
  }
}

function write(next: TrainerResources): void {
  cache = next;
  try {
    if (typeof window === "undefined") return;
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch {
    /* tiny blob; a quota failure here loses nothing critical */
  }
  if (typeof window !== "undefined") {
    window.dispatchEvent(new Event(TRAINER_RESOURCES_EVENT));
  }
}

/** Current snapshot (device-local, last-read-wins). */
export function getTrainerResources(): TrainerResources {
  return read();
}

/**
 * Record what a scanned page stated. Nulls are "not read this frame" and
 * never overwrite a known value.
 *
 * `observedAt` is when the game showed these numbers: omit it for a live
 * frame (now); pass the photo's time for an uploaded screenshot. A stardust
 * reading older than the one on record is dropped, so a stale screenshot can
 * never roll the balance back, and a screenshot newer than the last scan
 * wins over it.
 */
export function recordTrainerResources(obs: {
  stardust?: number | null;
  candyFamilySpeciesId?: number | null;
  candy?: number | null;
  candyXl?: number | null;
  /** Epoch ms the page was on screen; defaults to now (a live frame). */
  observedAt?: number;
  /** How the page reached us; defaults to "scan". */
  source?: "scan" | "screenshot";
  /** Mega Energy the page states, and the species it names it for. */
  megaEnergySpeciesId?: number | null;
  megaEnergy?: number | null;
  /** Every Mega Energy figure the page showed, X and Y apart (2026-09-03). */
  megaEnergies?: readonly MegaEnergyObservation[] | null;
}): void {
  const cur = read();
  const now = Date.now();
  const observedAt =
    typeof obs.observedAt === "number" && Number.isFinite(obs.observedAt) && obs.observedAt > 0
      ? Math.min(obs.observedAt, now)
      : now;
  const next: TrainerResources = {
    ...cur,
    candyByFamily: { ...cur.candyByFamily },
    megaEnergyBySpecies: { ...(cur.megaEnergyBySpecies ?? {}) },
    megaEnergyByForm: { ...(cur.megaEnergyByForm ?? {}) },
  };
  let changed = false;
  // Every Mega Energy figure on the page, keyed by form; the base form's
  // reading also lands in the per-species map. Newest sighting wins, so a
  // screenshot from before the last live scan cannot roll a figure back.
  const energies: MegaEnergyObservation[] = [...(obs.megaEnergies ?? [])];
  if (
    energies.length === 0 &&
    obs.megaEnergySpeciesId != null &&
    obs.megaEnergy != null
  ) {
    energies.push({ speciesId: obs.megaEnergySpeciesId, formLabel: "", energy: obs.megaEnergy });
  }
  for (const e of energies) {
    if (!Number.isFinite(e.energy) || e.energy < 0) continue;
    const key = megaEnergyFormKey(e.speciesId, e.formLabel);
    const prevForm = next.megaEnergyByForm![key];
    if (!prevForm || observedAt >= prevForm.updatedAt) {
      if (!prevForm || prevForm.energy !== e.energy || observedAt > prevForm.updatedAt) {
        next.megaEnergyByForm![key] = { energy: e.energy, updatedAt: observedAt };
        changed = true;
      }
    }
    if (e.formLabel === "" || e.formLabel === "X") {
      const prev = next.megaEnergyBySpecies![e.speciesId];
      if (!prev || observedAt >= prev.updatedAt) {
        if (!prev || prev.energy !== e.energy || observedAt > prev.updatedAt) {
          next.megaEnergyBySpecies![e.speciesId] = { energy: e.energy, updatedAt: observedAt };
          changed = true;
        }
      }
    }
  }
  if (
    obs.stardust != null &&
    observedAt >= cur.stardustUpdatedAt &&
    (obs.stardust !== cur.stardust || observedAt > cur.stardustUpdatedAt)
  ) {
    next.stardust = obs.stardust;
    next.stardustUpdatedAt = observedAt;
    next.stardustSource = obs.source ?? "scan";
    changed = true;
  }
  if (obs.candyFamilySpeciesId != null && (obs.candy != null || obs.candyXl != null)) {
    const prev = next.candyByFamily[obs.candyFamilySpeciesId];
    const entry: FamilyCandy = {
      candy: obs.candy ?? prev?.candy ?? null,
      candyXl: obs.candyXl ?? prev?.candyXl ?? null,
      updatedAt: now,
    };
    if (
      entry.candy !== prev?.candy ||
      entry.candyXl !== prev?.candyXl
    ) {
      next.candyByFamily[obs.candyFamilySpeciesId] = entry;
      changed = true;
    }
  }
  if (changed) write(next);
}

/**
 * Manual stardust entry (Stardust Planner's edit affordance) for trainers
 * who haven't scanned recently. Clamped to a plausible balance; the next
 * scan sighting overwrites it, since the game is the source of truth.
 */
export function setStardustManually(value: number): boolean {
  if (!Number.isFinite(value) || value < 0) return false;
  const cur = read();
  write({
    ...cur,
    candyByFamily: { ...cur.candyByFamily },
    megaEnergyBySpecies: { ...(cur.megaEnergyBySpecies ?? {}) },
    stardust: Math.min(99_999_999, Math.round(value)),
    stardustUpdatedAt: Date.now(),
    stardustSource: "manual",
  });
  return true;
}

/** Test seam. */
export function __resetTrainerResourcesForTest(): void {
  cache = null;
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* fine */
  }
}

/* ── Mega Energy per form ────────────────────────────────────────────────── */

/**
 * The store key for a species' Mega Energy figure with the letter GO prints
 * beside it: the base Mega (and X of a pair) is the species key, Y is the
 * species' Y form key, matching megaFormKey in megaLevels.
 */
export function megaEnergyFormKey(speciesId: number, formLabel: string): string {
  if (formLabel.toUpperCase() === "Y") {
    const y = MEGA_Y_FORM_ID_BY_SPECIES[speciesId];
    if (y) return `${speciesId}:${y}`;
  }
  return String(speciesId);
}

/** Y-form ids for the X/Y pairs; mirrors MEGA_EXTRA_FORMS without importing the sprite table here. */
const MEGA_Y_FORM_ID_BY_SPECIES: Readonly<Record<number, string>> = {
  6: "charizard-mega-y",
  150: "mewtwo-mega-y",
  26: "raichu-mega-y",
};

/**
 * The Mega Energy the trainer holds for one Mega form (key as megaFormKey),
 * or null when never read. The base form falls back to the older
 * per-species figure so readings from before the per-form split still show.
 */
export function megaEnergyForFormKey(formKey: string): { energy: number; updatedAt: number } | null {
  const r = getTrainerResources();
  const byForm = r.megaEnergyByForm?.[formKey];
  if (byForm) return byForm;
  if (!formKey.includes(":")) {
    const bySpecies = r.megaEnergyBySpecies?.[Number(formKey)];
    if (bySpecies) return bySpecies;
  }
  return null;
}
